import { useState, useEffect, useMemo } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Plus, Save, X, Trash2, Edit, ArrowLeft, FileText, TrendingUp, AlertTriangle, ChevronDown, ChevronUp, Printer } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../components/ConfirmDialog";

const STATUS_COLORS = { green: "#00CC52", yellow: "#EAB308", orange: "#F97316", red: "#EF4444" };
const STATUS_BG = { green: "bg-[#00CC52]/10 text-[#00CC52]", yellow: "bg-yellow-500/10 text-yellow-500", orange: "bg-orange-500/10 text-orange-500", red: "bg-red-500/10 text-red-500" };
const PHASES = ["baseline", "mid_program", "re_test", "discharge", "follow_up"];
const DIRECTIONS = ["higher_better", "lower_better", "within_range", "symmetry"];

function StatusDot({ status }) {
  return <div className="w-3 h-3 rounded-full" style={{ backgroundColor: STATUS_COLORS[status] || "#666" }} />;
}

export default function AssessmentsTab({ clientId }) {
  const confirm = useConfirm();
  const [assessments, setAssessments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [view, setView] = useState("list");
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [trendMetric, setTrendMetric] = useState("");
  const [trendMode, setTrendMode] = useState("combined");
  const [form, setForm] = useState({ name: "", type: "custom", phase: "baseline", program_id: "", date: "", trainer_notes: "", client_summary: "", focus_areas: "", metrics: [] });
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const [a, t, p] = await Promise.all([
      api.get(`/assessments?client_id=${clientId}`),
      api.get("/assessment-templates"),
      api.get(`/programs?client_id=${clientId}`),
    ]);
    setAssessments(a.data); setTemplates(t.data); setPrograms(p.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return d; } };

  // Which programs already have a baseline?
  const baselineByProgram = useMemo(() => {
    const map = {};
    assessments.forEach(a => { if (a.phase === "baseline" && a.program_id) map[a.program_id] = a; });
    return map;
  }, [assessments]);

  // When program changes or when starting a reassessment, prefill metrics from program.baseline_metrics
  const prefillFromBaseline = (programId) => {
    const p = programs.find(x => x.id === programId);
    if (!p) return [];
    return (p.baseline_metrics || []).map(bm => ({
      id: Math.random().toString(36).substr(2, 9),
      name: bm.name, unit: bm.unit, direction: bm.direction || "higher_better",
      thresholds: bm.thresholds || {},
      target_value: bm.target_value ?? null,
      target_left: bm.target_left ?? null,
      target_right: bm.target_right ?? null,
      target_range: bm.target_range || null,
      value: null,
      left_value: bm.has_laterality ? null : undefined,
      right_value: bm.has_laterality ? null : undefined,
      aggregation: "single",
      from_baseline: true,
    }));
  };

  const startNewBaseline = (programId) => {
    const p = programs.find(x => x.id === programId);
    setEditId(null);
    setForm({ name: p ? `${p.title} — Baseline` : "Baseline Assessment", type: "custom",
      phase: "baseline", program_id: programId, date: new Date().toISOString().split("T")[0],
      trainer_notes: "", client_summary: "", focus_areas: "", metrics: [] });
    setShowForm(true);
  };

  const startNewReassessment = (programId) => {
    const p = programs.find(x => x.id === programId);
    const metrics = prefillFromBaseline(programId);
    const count = assessments.filter(a => a.program_id === programId && a.phase !== "baseline").length + 1;
    setEditId(null);
    setForm({ name: p ? `${p.title} — Reassessment #${count}` : "Reassessment", type: "custom",
      phase: "reassessment", program_id: programId, date: new Date().toISOString().split("T")[0],
      trainer_notes: "", client_summary: "", focus_areas: "", metrics });
    setShowForm(true);
  };

  const applyTemplate = (tid) => {
    const tmpl = templates.find(t => t.id === tid);
    if (!tmpl) return;
    const metrics = tmpl.metrics.map(m => ({
      id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9),
      name: m.name, unit: m.unit, value: null, left_value: m.has_laterality ? null : undefined,
      right_value: m.has_laterality ? null : undefined, direction: m.direction, aggregation: m.aggregation || "single",
      thresholds: m.default_thresholds || {}, target_value: null, target_range: null
    }));
    setForm(f => ({ ...f, metrics, name: f.name || tmpl.name, type: tmpl.name.toLowerCase().includes("lower") ? "lower_body" : tmpl.name.toLowerCase().includes("upper") ? "upper_body" : "full_body" }));
  };

  const addCustomMetric = () => {
    setForm(f => ({ ...f, metrics: [...f.metrics, {
      id: Math.random().toString(36).substr(2, 9), name: "", unit: "score", value: null,
      left_value: null, right_value: null, direction: "higher_better", aggregation: "single",
      thresholds: { green: { min: 75, max: 100 }, yellow: { min: 60, max: 74 }, orange: { min: 45, max: 59 }, red: { min: 0, max: 44 } },
      target_value: null, target_range: null
    }] }));
  };

  const updateMetric = (idx, field, val) => {
    setForm(f => {
      const m = [...f.metrics]; m[idx] = { ...m[idx], [field]: val }; return { ...f, metrics: m };
    });
  };

  const removeMetric = (idx) => { setForm(f => ({ ...f, metrics: f.metrics.filter((_, i) => i !== idx) })); };

  const save = async () => {
    if (!form.program_id) { toast.error("Please select a program"); return; }
    const cleanMetrics = (form.metrics || [])
      .filter(m => (m.name || "").trim())
      .map(m => ({ ...m, name: m.name.trim(),
        value: m.value != null && m.value !== "" ? Number(m.value) : null,
        left_value: m.left_value != null && m.left_value !== "" ? Number(m.left_value) : null,
        right_value: m.right_value != null && m.right_value !== "" ? Number(m.right_value) : null,
        target_value: m.target_value != null && m.target_value !== "" ? Number(m.target_value) : null,
        target_left: m.target_left != null && m.target_left !== "" ? Number(m.target_left) : null,
        target_right: m.target_right != null && m.target_right !== "" ? Number(m.target_right) : null,
      }));
    if (!form.name?.trim()) { toast.error("Assessment name is required"); return; }
    if (cleanMetrics.length === 0) { toast.error("Add at least one metric with a name"); return; }
    const payload = { ...form, name: form.name.trim(), client_id: clientId,
      focus_areas: form.focus_areas.split(",").map(s => s.trim()).filter(Boolean),
      metrics: cleanMetrics };
    try {
      if (editId) { await api.put(`/assessments/${editId}`, payload); }
      else { await api.post("/assessments", payload); }
      toast.success(editId ? "Assessment updated" : "Assessment saved");
      setShowForm(false); setEditId(null);
      setForm({ name: "", type: "custom", phase: "baseline", program_id: "", date: "", trainer_notes: "", client_summary: "", focus_areas: "", metrics: [] });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save assessment");
    }
  };

  const startEdit = (a) => {
    setForm({ name: a.name, type: a.type, phase: a.phase, date: a.date, trainer_notes: a.trainer_notes || "",
      client_summary: a.client_summary || "", focus_areas: (a.focus_areas || []).join(", "), metrics: a.metrics || [] });
    setEditId(a.id); setShowForm(true); setView("list");
  };

  const del = async (id) => {
    const a = assessments.find(x => x.id === id);
    const ok = await confirm({
      title: a?.phase === "baseline" ? "Delete baseline assessment?" : "Delete assessment?",
      description: a?.phase === "baseline"
        ? `This will remove the baseline and its locked metric template for this program. All reassessments will lose their reference point. This cannot be undone.`
        : `"${a?.name || "This assessment"}" will be permanently removed.`,
      confirmText: "Delete assessment",
    });
    if (!ok) return;
    await api.delete(`/assessments/${id}`);
    if (selected?.id === id) setSelected(null);
    load();
  };

  const allMetricNames = useMemo(() => {
    const names = new Set();
    assessments.forEach(a => (a.metrics || []).forEach(m => {
      if (m && typeof m.name === "string" && m.name.trim()) names.add(m.name);
    }));
    return Array.from(names);
  }, [assessments]);

  const trendData = useMemo(() => {
    if (!trendMetric) return [];
    return assessments.slice().reverse().map(a => {
      const m = (a.metrics || []).find(x => x.name === trendMetric);
      if (!m) return null;
      return { date: fmtDate(a.date), phase: a.phase, value: m.value, left: m.left_value, right: m.right_value,
        asymmetry: m.asymmetry_pct, status: m.status };
    }).filter(Boolean);
  }, [assessments, trendMetric]);

  const openReport = async (a) => {
    const { data } = await api.get(`/assessments/${a.id}/report`);
    const prev = data.previous;
    // Escape user-controlled values before embedding into HTML to prevent XSS.
    const esc = (v) => String(v ?? "")
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
    const safeStatus = (s) => /^(green|yellow|orange|red)$/.test(s || "") ? s : "green";
    const metricsHtml = (data.assessment.metrics || []).map((m) => {
      const prevM = prev ? (prev.metrics || []).find((x) => x.name === m.name) : null;
      const change = prevM && typeof m.value === "number" && typeof prevM.value === "number" ? (m.value - prevM.value) : null;
      const changeColor = change == null ? ""
        : (change >= 0 && m.direction === "higher_better") || (change <= 0 && m.direction === "lower_better") ? "#00CC52" : "#EF4444";
      return `<div class="metric"><div><span class="dot ${safeStatus(m.status)}"></span><strong>${esc(m.name)}</strong>`
        + (m.left_value != null ? ` (L: ${esc(m.left_value)} / R: ${esc(m.right_value)})` : "")
        + (m.asymmetry_pct ? ` <small style="color:#666">${esc(m.asymmetry_pct)}% asymmetry</small>` : "")
        + `</div><div><strong>${m.value != null ? esc(m.value) : "—"} ${esc(m.unit || "")}</strong>`
        + (change != null ? ` <small style="color:${changeColor}">(${change >= 0 ? "+" : ""}${esc(change.toFixed(1))})</small>` : "")
        + `</div></div>`;
    }).join("");
    const focusHtml = data.assessment.focus_areas?.length
      ? `<h2>Focus Areas</h2><ul>${data.assessment.focus_areas.map((f) => `<li>${esc(f)}</li>`).join("")}</ul>` : "";
    const summaryHtml = data.assessment.client_summary
      ? `<div class="section"><h2>Summary</h2><p>${esc(data.assessment.client_summary)}</p></div>` : "";
    const notesHtml = data.assessment.trainer_notes ? `<h2>Trainer Notes</h2><p>${esc(data.assessment.trainer_notes)}</p>` : "";
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Assessment Report - ${esc(data.client?.name)}</title>
      <style>body{font-family:Arial,sans-serif;max-width:800px;margin:0 auto;padding:40px;color:#1a1a1c}
      h1{font-size:24px;margin-bottom:4px}h2{font-size:18px;color:#007AFF;margin-top:30px}
      .header{border-bottom:3px solid #007AFF;padding-bottom:20px;margin-bottom:30px}
      .metric{display:flex;align-items:center;justify-content:space-between;padding:12px 0;border-bottom:1px solid #e5e5e5}
      .dot{width:12px;height:12px;border-radius:50%;display:inline-block;margin-right:8px}
      .green{background:#00CC52}.yellow{background:#EAB308}.orange{background:#F97316}.red{background:#EF4444}
      .section{margin-bottom:24px}table{width:100%;border-collapse:collapse}td,th{padding:8px;text-align:left;border-bottom:1px solid #eee}
      @media print{body{padding:20px}}</style></head><body>
      <div class="header"><h1>FIT FUSION ANALYTICS</h1><p style="color:#666">Assessment Report</p></div>
      <table><tr><td><strong>Client:</strong> ${esc(data.client?.name)}</td><td><strong>Date:</strong> ${esc(data.assessment.date)}</td></tr>
      <tr><td><strong>Assessment:</strong> ${esc(data.assessment.name)}</td><td><strong>Phase:</strong> ${esc(data.assessment.phase)}</td></tr></table>
      ${summaryHtml}<h2>Results</h2>${metricsHtml}${focusHtml}${notesHtml}
      <div style="margin-top:40px;padding-top:20px;border-top:2px solid #007AFF;color:#666;font-size:12px">
      <p>Fit Fusion Analytics | Otto L. Thiel | Generated ${esc(new Date().toLocaleDateString())}</p></div></body></html>`;
    // Safer than document.write: use a Blob URL so the browser parses the HTML document directly.
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (w) {
      // Trigger print once the popup finishes loading.
      w.addEventListener("load", () => { try { w.print(); } catch (e) { console.warn("print failed", e); } });
      // Revoke the object URL after a short delay so the window can finish loading.
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    }
  };

  // ===== DETAIL VIEW =====
  if (selected && view === "detail") {
    const prev = assessments.find(a => a.date < selected.date && a.id !== selected.id);
    return (
      <div className="space-y-6" data-testid="assessment-detail">
        <div className="flex items-center justify-between">
          <button onClick={() => { setSelected(null); setView("list"); }} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Back to Assessments</button>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => openReport(selected)} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs" data-testid="generate-report"><Printer className="w-3 h-3 mr-1" /> Generate Report</Button>
            <Button size="sm" variant="ghost" onClick={() => startEdit(selected)} className="rounded-none text-xs"><Edit className="w-3 h-3 mr-1" /> Edit</Button>
            <Button size="sm" variant="ghost" onClick={() => { del(selected.id); setView("list"); }} className="rounded-none text-xs text-destructive"><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
          </div>
        </div>
        <div className="bg-card border border-border p-6">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-xl font-bold">{selected.name}</h3>
            <Badge className="text-[10px] capitalize">{selected.phase?.replace("_", " ")}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mb-4">{fmtDate(selected.date)}</p>
          {selected.client_summary && <div className="bg-[#007AFF]/5 border border-[#007AFF]/20 p-4 mb-4"><p className="text-sm">{selected.client_summary}</p></div>}
          {selected.focus_areas?.length > 0 && <div className="flex flex-wrap gap-2 mb-4">{selected.focus_areas.map(f => <Badge key={f} variant="outline" className="text-xs">{f}</Badge>)}</div>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(selected.metrics || []).map(m => {
            const prevM = prev ? (prev.metrics || []).find(x => x.name === m.name) : null;
            const change = prevM && m.value != null && prevM.value != null ? m.value - prevM.value : null;
            return (
              <div key={m.id || m.name} className="bg-card border border-border p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2"><StatusDot status={m.status} /><span className="text-sm font-bold">{m.name}</span></div>
                  <span className="text-xs text-muted-foreground">{m.unit}</span>
                </div>
                <p className="text-3xl font-black">{m.value != null ? m.value : "—"}</p>
                {m.left_value != null && (
                  <div className="flex gap-4 mt-2 text-xs">
                    <span>L: <strong>{m.left_value}</strong></span><span>R: <strong>{m.right_value}</strong></span>
                    {m.asymmetry_pct != null && <span className={m.asymmetry_pct > 15 ? "text-red-500" : m.asymmetry_pct > 10 ? "text-orange-500" : "text-muted-foreground"}>{m.asymmetry_pct}% asym ({m.stronger_side})</span>}
                  </div>
                )}
                {change != null && (
                  <p className={`text-xs mt-2 font-medium ${(change >= 0 && m.direction === "higher_better") || (change <= 0 && m.direction === "lower_better") ? "text-[#00CC52]" : "text-red-500"}`}>
                    {change >= 0 ? "+" : ""}{change.toFixed(1)} from previous
                  </p>
                )}
                <div className={`mt-2 inline-block px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_BG[m.status] || ""}`}>{m.status || "N/A"}</div>
              </div>
            );
          })}
        </div>
        {selected.trainer_notes && <div className="bg-card border border-border p-5"><h4 className="font-bold text-sm mb-2">Internal Notes</h4><p className="text-sm text-muted-foreground">{selected.trainer_notes}</p></div>}
      </div>
    );
  }

  // ===== FORM VIEW =====
  if (showForm) {
    const selectedProgram = programs.find(p => p.id === form.program_id);
    const isReassess = form.phase === "reassessment";
    return (
      <div className="space-y-5" data-testid="assessment-form">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg">{editId ? "Edit Assessment" : isReassess ? "New Reassessment" : "New Baseline Assessment"}</h3>
            {selectedProgram && <p className="text-xs text-muted-foreground mt-0.5">Program: <span className="font-bold text-foreground">{selectedProgram.title}</span></p>}
          </div>
          <Button size="sm" variant="ghost" onClick={() => { setShowForm(false); setEditId(null); }} className="rounded-none text-xs"><X className="w-3 h-3 mr-1" /> Cancel</Button>
        </div>

        {isReassess && (
          <div className="bg-[#007AFF]/5 border-l-2 border-[#007AFF] px-4 py-3 text-xs">
            Metrics, units, directions, and targets are locked to the baseline for this program. Only enter the new measurements below. You can add extra one-off metrics if needed.
          </div>
        )}
        {!isReassess && !editId && templates.length > 0 && form.metrics.length === 0 && (
          <div className="bg-secondary/30 border border-border p-4">
            <Label className="text-xs font-bold">Start from template (baseline)</Label>
            <div className="flex gap-2 mt-2 flex-wrap">
              {templates.map(t => <Button key={t.id} size="sm" variant="outline" onClick={() => applyTemplate(t.id)} className="rounded-none text-xs">{t.name}</Button>)}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Assessment Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 rounded-none bg-card border-border text-sm" data-testid="assessment-name-input" /></div>
          <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="mt-1 rounded-none bg-card border-border text-sm" data-testid="assessment-date-input" /></div>
        </div>
        <div><Label className="text-xs">Focus Areas (comma-separated)</Label><Input value={form.focus_areas} onChange={e => setForm({...form, focus_areas: e.target.value})} placeholder="hamstring activation, sprint mechanics..." className="mt-1 rounded-none bg-card border-border text-sm" /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Internal Notes (trainer only)</Label><Textarea value={form.trainer_notes} onChange={e => setForm({...form, trainer_notes: e.target.value})} rows={2} className="mt-1 rounded-none bg-card border-border text-sm resize-none" /></div>
          <div><Label className="text-xs">Client Summary (visible to client)</Label><Textarea value={form.client_summary} onChange={e => setForm({...form, client_summary: e.target.value})} rows={2} className="mt-1 rounded-none bg-card border-border text-sm resize-none" /></div>
        </div>

        <div className="border-t border-border pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-sm">Metrics ({form.metrics.length})</h4>
            <Button size="sm" onClick={addCustomMetric} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs" data-testid="add-metric-btn"><Plus className="w-3 h-3 mr-1" /> Add Metric</Button>
          </div>
          {form.metrics.length === 0 && <p className="text-xs text-muted-foreground italic mb-3">No metrics yet. {isReassess ? "Your program's baseline has no saved metric template." : "Add one below, or use a template above."}</p>}
          <div className="space-y-3">
            {form.metrics.map((m, i) => {
              const locked = isReassess && m.from_baseline;
              return (
                <div key={m.id || i} className={`bg-card border border-border p-4 ${locked ? "border-l-2 border-l-[#007AFF]" : ""}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-muted-foreground">METRIC {i + 1}</span>
                      {locked && <span className="text-[10px] px-1.5 py-0.5 bg-[#007AFF]/10 text-[#007AFF] font-bold">FROM BASELINE</span>}
                    </div>
                    {!locked && <Button size="sm" variant="ghost" onClick={() => removeMetric(i)} className="h-6 w-6 p-0 text-destructive"><Trash2 className="w-3 h-3" /></Button>}
                  </div>
                  {/* Metric definition (locked on reassessment) */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                    <div><Label className="text-[10px]">Name</Label>
                      <Input value={m.name || ""} disabled={locked} onChange={e => updateMetric(i, "name", e.target.value)} className="mt-0.5 rounded-none bg-background border-border text-xs h-8" data-testid={`metric-name-${i}`} /></div>
                    <div><Label className="text-[10px]">Unit</Label>
                      <Input value={m.unit || ""} disabled={locked} onChange={e => updateMetric(i, "unit", e.target.value)} className="mt-0.5 rounded-none bg-background border-border text-xs h-8" /></div>
                    <div><Label className="text-[10px]">Direction</Label>
                      <Select value={m.direction} disabled={locked} onValueChange={v => updateMetric(i, "direction", v)}>
                        <SelectTrigger className="mt-0.5 rounded-none bg-background border-border text-xs h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>{DIRECTIONS.map(d => <SelectItem key={d} value={d}>{d.replace("_", " ")}</SelectItem>)}</SelectContent>
                      </Select></div>
                  </div>
                  {/* Measured values (always editable) */}
                  <div className="bg-secondary/30 border border-border p-2 mb-2">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Measured — {form.date || "today"}</p>
                    <div className="grid grid-cols-3 gap-2">
                      <div><Label className="text-[10px]">Value (single)</Label><Input type="number" value={m.value ?? ""} onChange={e => updateMetric(i, "value", e.target.value === "" ? null : Number(e.target.value))} className="mt-0.5 rounded-none bg-background border-border text-xs h-8" data-testid={`metric-value-${i}`} /></div>
                      <div><Label className="text-[10px]">Left</Label><Input type="number" value={m.left_value ?? ""} onChange={e => updateMetric(i, "left_value", e.target.value === "" ? null : Number(e.target.value))} className="mt-0.5 rounded-none bg-background border-border text-xs h-8" /></div>
                      <div><Label className="text-[10px]">Right</Label><Input type="number" value={m.right_value ?? ""} onChange={e => updateMetric(i, "right_value", e.target.value === "" ? null : Number(e.target.value))} className="mt-0.5 rounded-none bg-background border-border text-xs h-8" /></div>
                    </div>
                  </div>
                  {/* Targets (locked on reassessment) */}
                  <div className="bg-[#00CC52]/5 border border-[#00CC52]/30 p-2">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#00CC52] mb-1.5">{locked ? "Target (from baseline)" : "Target / Goal"}</p>
                    <div className="grid grid-cols-3 gap-2">
                      <div><Label className="text-[10px]">Target (single)</Label><Input type="number" value={m.target_value ?? ""} disabled={locked} onChange={e => updateMetric(i, "target_value", e.target.value === "" ? null : Number(e.target.value))} className="mt-0.5 rounded-none bg-background border-border text-xs h-8" data-testid={`metric-target-${i}`} /></div>
                      <div><Label className="text-[10px]">Target L</Label><Input type="number" value={m.target_left ?? ""} disabled={locked} onChange={e => updateMetric(i, "target_left", e.target.value === "" ? null : Number(e.target.value))} className="mt-0.5 rounded-none bg-background border-border text-xs h-8" /></div>
                      <div><Label className="text-[10px]">Target R</Label><Input type="number" value={m.target_right ?? ""} disabled={locked} onChange={e => updateMetric(i, "target_right", e.target.value === "" ? null : Number(e.target.value))} className="mt-0.5 rounded-none bg-background border-border text-xs h-8" /></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <Button onClick={save} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none font-bold text-sm px-6" data-testid="assessment-save"><Save className="w-4 h-4 mr-2" />{editId ? "Update" : "Save"} {isReassess ? "Reassessment" : "Baseline"}</Button>
        </div>
      </div>
    );
  }

  // ===== LIST VIEW =====
  const activeProgram = programs.find(p => p.status === "in_progress");
  const hasBaseline = activeProgram && baselineByProgram[activeProgram.id];

  return (
    <div className="space-y-6" data-testid="assessments-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-bold text-lg">Assessments</h3>
          {activeProgram ? (
            <p className="text-xs text-muted-foreground mt-0.5">Active program: <span className="font-bold text-foreground">{activeProgram.title}</span></p>
          ) : (
            <p className="text-xs text-muted-foreground mt-0.5">No active program — create a program before adding assessments.</p>
          )}
        </div>
        <div className="flex gap-2">
          {activeProgram && !hasBaseline && (
            <Button size="sm" onClick={() => startNewBaseline(activeProgram.id)} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs" data-testid="add-baseline-btn">
              <Plus className="w-3 h-3 mr-1" /> New Baseline
            </Button>
          )}
          {activeProgram && hasBaseline && (
            <Button size="sm" onClick={() => startNewReassessment(activeProgram.id)} className="bg-[#00CC52] hover:bg-[#00CC52]/80 text-white rounded-none text-xs" data-testid="add-reassessment-btn">
              <Plus className="w-3 h-3 mr-1" /> New Reassessment
            </Button>
          )}
        </div>
      </div>

      {assessments.length === 0 ? <p className="text-muted-foreground text-sm">No assessments yet. Create the baseline for the active program to start tracking progress.</p> : (
        <>
          <div className="space-y-2">
            {assessments.map(a => {
              const greens = (a.metrics || []).filter(m => m.status === "green").length;
              const total = (a.metrics || []).length;
              return (
                <div key={a.id} className="bg-card border border-border p-4 cursor-pointer hover:border-[#007AFF]/30 transition-colors"
                  onClick={() => { setSelected(a); setView("detail"); }} data-testid={`assessment-row-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-3">
                        <h4 className="font-bold text-sm">{a.name}</h4>
                        <Badge className="text-[10px] capitalize">{a.phase?.replace("_", " ")}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{fmtDate(a.date)} &middot; {total} metrics</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">{(a.metrics || []).slice(0, 6).map((m) => <StatusDot key={m.id || `${a.id}-${m.name}`} status={m.status} />)}</div>
                      <span className="text-xs text-muted-foreground">{greens}/{total} optimal</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {allMetricNames.length > 0 && (
            <div className="bg-card border border-border p-5">
              <h4 className="font-bold text-sm mb-4">Trend Analysis</h4>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Select value={trendMetric} onValueChange={setTrendMetric}>
                  <SelectTrigger className="w-48 rounded-none bg-background border-border text-xs" data-testid="trend-metric-select"><SelectValue placeholder="Select metric..." /></SelectTrigger>
                  <SelectContent>{allMetricNames.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                </Select>
                <div className="flex gap-1">
                  {["combined", "left", "right", "asymmetry"].map(m => (
                    <button key={m} onClick={() => setTrendMode(m)}
                      className={`px-3 py-1 text-xs font-medium border transition-colors ${trendMode === m ? "bg-[#007AFF] text-white border-[#007AFF]" : "bg-card border-border hover:border-[#007AFF]/50"}`}
                      data-testid={`trend-mode-${m}`}>{m === "combined" ? "Combined" : m === "left" ? "Left" : m === "right" ? "Right" : "Asymmetry"}</button>
                  ))}
                </div>
              </div>
              {trendMetric && trendData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 0, fontSize: 12 }} />
                    <Legend />
                    {trendMode === "combined" && <Line type="monotone" dataKey="value" stroke="#007AFF" strokeWidth={2} name="Value" dot={{ fill: "#007AFF", r: 4 }} />}
                    {trendMode === "left" && <Line type="monotone" dataKey="left" stroke="#007AFF" strokeWidth={2} name="Left" dot={{ fill: "#007AFF", r: 4 }} />}
                    {trendMode === "right" && <Line type="monotone" dataKey="right" stroke="#00CC52" strokeWidth={2} name="Right" dot={{ fill: "#00CC52", r: 4 }} />}
                    {trendMode === "asymmetry" && <Line type="monotone" dataKey="asymmetry" stroke="#F97316" strokeWidth={2} name="Asymmetry %" dot={{ fill: "#F97316", r: 4 }} />}
                  </LineChart>
                </ResponsiveContainer>
              ) : trendMetric ? <p className="text-xs text-muted-foreground text-center py-8">Not enough data points for this metric.</p> : null}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Client-facing simplified assessment view
export function ClientAssessmentView({ assessments }) {
  if (!assessments || assessments.length === 0) return <p className="text-muted-foreground text-sm">No assessments available yet.</p>;
  const latest = assessments[0];
  const prev = assessments.length > 1 ? assessments[1] : null;
  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return d; } };

  const getInsight = (m, prevM) => {
    const change = prevM && m.value != null && prevM.value != null ? m.value - prevM.value : null;
    if (change === null) return m.status === "green" ? "Looking good" : m.status === "yellow" ? "Room to improve" : m.status === "red" ? "Focus area" : "On track";
    const improved = (change > 0 && m.direction === "higher_better") || (change < 0 && m.direction === "lower_better");
    if (improved) return `Improved by ${Math.abs(change).toFixed(0)} ${m.unit}`;
    if (change === 0) return "Holding steady";
    return "Needs attention";
  };

  return (
    <div className="space-y-6" data-testid="client-assessments">
      <div className="bg-card border border-border p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold">{latest.name}</h3>
          <Badge className="text-[10px] capitalize">{latest.phase?.replace("_", " ")}</Badge>
        </div>
        <p className="text-xs text-muted-foreground mb-4">{fmtDate(latest.date)}</p>
        {latest.client_summary && <div className="bg-[#007AFF]/5 border border-[#007AFF]/20 p-4 mb-4"><p className="text-sm">{latest.client_summary}</p></div>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {(latest.metrics || []).map(m => {
          const prevM = prev ? (prev.metrics || []).find(x => x.name === m.name) : null;
          return (
            <div key={m.id || m.name} className="bg-card border border-border p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold">{m.name}</span>
                <div className={`px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_BG[m.status] || ""}`}>{m.status === "green" ? "Optimal" : m.status === "yellow" ? "Good" : m.status === "orange" ? "Fair" : "Needs Work"}</div>
              </div>
              <p className="text-3xl font-black">{m.value != null ? m.value : "—"}<span className="text-sm text-muted-foreground ml-1">{m.unit}</span></p>
              <p className="text-xs text-muted-foreground mt-2">{getInsight(m, prevM)}</p>
            </div>
          );
        })}
      </div>
      {assessments.length > 1 && (
        <div className="bg-card border border-border p-5">
          <h4 className="font-bold text-sm mb-3">Your Progress Over Time</h4>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={assessments.slice().reverse().map(a => {
              const row = { date: fmtDate(a.date) };
              (a.metrics || []).forEach(m => { row[m.name] = m.value; });
              return row;
            })}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 0, fontSize: 12 }} />
              {(latest.metrics || []).slice(0, 4).map((m, i) => (
                <Line key={m.name} type="monotone" dataKey={m.name} stroke={["#007AFF", "#00CC52", "#FFB800", "#FF4D6A"][i]} strokeWidth={2} name={m.name} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
